import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";
import { LogIn, Dumbbell, Clock } from "lucide-react";
import DetailSheet from "../session/generate/DetailSheet";
import { getSessions } from "../../services/api";
import { formatDuration, calculateTotalDuration } from "../../utils/time";
import { buildProgrammeContext } from "../../utils/programmeContext";
import { buildSlotGenerationDefaults } from "../../config/periodisation";
import GenerateSessionDialog from "../session/GenerateSessionDialog";

// Fills a programme slot, by building it (the generate screen, prefilled from
// the slot and given the programme's context) or from a saved session.
//
// `mode` picks which: the session sheet has already asked. `autoGenerate`
// skips the form and builds straight away. `onAssigned(plan)` runs after the
// slot is filled with a freshly built plan; when given, the plan is left
// loaded (the caller is about to start it) instead of being cleared.
const InstantiateSessionDialog = ({
  open,
  onOpenChange,
  targetSession,
  programme,
  onAssignSession,
  mode = "generate",
  autoGenerate = false,
  onAssigned,
}) => {
  const { workoutPlan, clearSession } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();

  const [generateOpen, setGenerateOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [awaitingGeneration, setAwaitingGeneration] = useState(false);
  // `workoutPlan` is a persisted, app-wide localStorage value (it survives page
  // reloads and programme deletes) that the guided/standalone flows also write.
  // We snapshot its reference the instant generation starts so the intercept
  // below only ever captures a FRESHLY generated plan (a new object) for this
  // slot — never a stale plan that was already sitting there. Without this the
  // stale plan gets copied into every slot the user opens (#56).
  const generationBaselineRef = useRef(null);

  const scheduledLabel = targetSession?.scheduled_label || "Session";

  // Opening in generate mode arms the intercept and opens the generate screen.
  // The baseline is whatever plan is loaded right now, so only a plan built
  // after this point is ever assigned to the slot (#56).
  useEffect(() => {
    if (!open) {
      setGenerateOpen(false);
      setAwaitingGeneration(false);
      generationBaselineRef.current = null;
      return;
    }
    if (mode !== "generate" || !isAuthenticated) return;
    generationBaselineRef.current = workoutPlan;
    setAwaitingGeneration(true);
    setGenerateOpen(true);
    // Arm once per opening; later plan changes are the intercept's business.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, isAuthenticated]);

  // Load saved sessions in "load" mode
  useEffect(() => {
    if (!open || mode !== "load") return;
    setLoadingSessions(true);
    if (isAuthenticated) {
      getSessions({ limit: 50 })
        .then((data) =>
          setSessions((data.sessions || data || []).filter((s) => s.plan_data)),
        )
        .catch(() => setSessions([]))
        .finally(() => setLoadingSessions(false));
    } else {
      try {
        const stored = JSON.parse(
          localStorage.getItem("completedSessions") || "[]",
        );
        setSessions(stored.filter((s) => s.plan_data));
      } catch {
        setSessions([]);
      }
      setLoadingSessions(false);
    }
  }, [open, mode, isAuthenticated]);

  // Intercept the plan produced by the reused GenerateSessionDialog and assign
  // it to this slot. Guard on a NEW plan reference (!== the baseline captured at
  // generation start) so a stale, pre-existing workoutPlan is never assigned
  // before the user has actually generated anything (#56).
  useEffect(() => {
    if (
      awaitingGeneration &&
      workoutPlan &&
      workoutPlan !== generationBaselineRef.current &&
      targetSession
    ) {
      const plan = workoutPlan;
      onAssignSession(targetSession.id, {
        label: plan.label || plan.type || scheduledLabel,
        plan_data: plan,
      });
      if (onAssigned) onAssigned(plan);
      else clearSession();
      setAwaitingGeneration(false);
      generationBaselineRef.current = null;
      onOpenChange(false);
    }
  }, [
    awaitingGeneration,
    workoutPlan,
    targetSession,
    onAssignSession,
    onAssigned,
    clearSession,
    onOpenChange,
    scheduledLabel,
  ]);

  // Programme background for the LLM: framework constraints, deload handling,
  // the surrounding week schedule, and how the same slot went last week — so
  // week 4's "Upper Body" builds on week 3's instead of being generated in a
  // vacuum. See utils/programmeContext.js (#54).
  const programmeContext = useMemo(
    () => buildProgrammeContext(programme, targetSession),
    [programme, targetSession],
  );

  // Structured prefills for the generate dialog (duration, styles, muscle
  // focus, goal) so its knobs reflect the slot instead of profile defaults.
  // The programme's own equipment set wins over the Profile default and the
  // last generation's kit (#196).
  const { equipmentPresets } = useUserProfile();
  const slotDefaults = useMemo(() => {
    const defaults = buildSlotGenerationDefaults(programme, targetSession);
    const preset = equipmentPresets?.find((p) => p.id === programme?.equipment_preset_id);
    if (!defaults || !Array.isArray(preset?.equipment)) return defaults;
    return { ...defaults, availableEquipment: [...preset.equipment] };
  }, [programme, targetSession, equipmentPresets]);

  const handleLoadSession = useCallback(
    (session) => {
      if (!targetSession) return;
      onAssignSession(targetSession.id, {
        label:
          session.plan_data?.label || session.workout_type || scheduledLabel,
        plan_data: session.plan_data,
      });
      onOpenChange(false);
    },
    [targetSession, onAssignSession, onOpenChange, scheduledLabel],
  );

  if (!isAuthenticated) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Sign in required</DialogTitle>
            <DialogDescription>
              Sign in to build sessions for your programme.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
            <LogIn className="h-6 w-6 text-primary shrink-0" />
            <p className="text-sm text-muted-foreground">
              Programme sessions require an account to persist your data.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <DetailSheet
        open={open && mode === "load"}
        onOpenChange={onOpenChange}
        title="Use a saved session"
        description={`For ${scheduledLabel}, week ${targetSession?.week_number}`}
        showDescription
      >
        {loadingSessions ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No saved sessions yet.
          </p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border divide-y divide-border">
            {sessions.map((session) => {
              const sessionLabel =
                session.plan_data?.label || session.workout_type || "Workout";
              const totalDur = session.plan_data
                ? calculateTotalDuration(session.plan_data)
                : 0;
              const formattedDur =
                totalDur > 0
                  ? formatDuration(totalDur)
                  : session.duration_minutes
                    ? `${session.duration_minutes} min`
                    : null;
              const date = session.created_at
                ? new Date(session.created_at).toLocaleDateString("en-AU", {
                    month: "short",
                    day: "numeric",
                  })
                : null;

              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => handleLoadSession(session)}
                  className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-muted/40"
                >
                  <Dumbbell className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-white">
                      {sessionLabel}
                    </span>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      {formattedDur && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formattedDur}
                        </span>
                      )}
                      {date && <span>{date}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DetailSheet>

      {/* Reused GenerateSessionDialog */}
      <GenerateSessionDialog
        open={generateOpen}
        onOpenChange={(v) => {
          setGenerateOpen(v);
          // Closed without producing a new plan (still the baseline reference) →
          // the user cancelled, so stop awaiting and close. When a fresh plan
          // WAS generated the intercept effect handles teardown instead.
          if (!v && workoutPlan === generationBaselineRef.current) {
            setAwaitingGeneration(false);
            generationBaselineRef.current = null;
            onOpenChange(false);
          }
        }}
        contextNotes={programmeContext}
        configOverrides={slotDefaults}
        autoGenerate={autoGenerate}
      />
    </>
  );
};

export default InstantiateSessionDialog;
